import React, { useState } from 'react';
import { useNotify, useQuery } from 'react-admin';
import { Button, Card, Modal, Spin, Tooltip, Typography } from 'antd';
import { CopyOutlined, FileTextOutlined, SyncOutlined } from '@ant-design/icons';
import { formatDateAndTimeFromNowWithTooltip } from '../../../helpers/utils';
import { getTagForStates } from '../../Common/Tags';
import './ChangeHistory.css';

const { Text } = Typography;
const spinnerElement = <Spin size="large" description="Loading"><Card style={{ height: '100px' }} /></Spin>;

/**
 * Activity feed for a single change instance: state transitions and log edits,
 * newest first. Class names are intentionally unscoped so the same component
 * works on the change instances list and the service item detail page.
 */
const ChangeHistory = ({ resource, id }: { resource?: string; id: number | string }) => {
    const notify = useNotify();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState('');

    // resource arrives through LazyExpandedRow's extraProps; without the guard
    // a missing one would fire a request at an undefined endpoint
    const { data: historyData } = useQuery(
        {
            type: 'getAction',
            resource: resource || '',
            payload: { id, verb: 'history', filter: {} },
        },
        { enabled: !!resource },
    );

    const actorPrefix = (r: any) => (
        r.changed_by
            ? <>
                <Text code>{r.changed_by}</Text>
                {r.changed_by_team ? <> from <Text code>{r.changed_by_team}</Text></> : ''}{' '}
            </>
            : null
    );

    const renderActivityText = (reason: string, r: any) => {
        if (reason === 'state') {
            return r.changed_by
                ? <>{actorPrefix(r)}changed state to {getTagForStates(r.state)}</>
                : <>State changed to {getTagForStates(r.state)}</>;
        }
        if (reason === 'log') {
            return r.changed_by
                ? <>{actorPrefix(r)}updated the log</>
                : <>Log updated</>;
        }
        return <>—</>;
    };

    const copyLog = () => {
        navigator.clipboard?.writeText(selectedLog || '')
            .then(() => notify('Log copied to clipboard'))
            .catch(() => notify('Could not copy', 'error'));
    };

    if (!historyData) return spinnerElement;

    const activities: any[] = historyData?.results || [];
    if (activities.length === 0) {
        return <div className="chg-hist chg-hist--empty">No activity recorded yet.</div>;
    }

    return (
        <>
            <div className="chg-hist">
                {activities.map((a: any, i: number) => (
                    <div className="chg-hist__item" key={i}>
                        <span className={`chg-hist__icon chg-hist__icon--${a.reason}`} aria-hidden="true">
                            {a.reason === 'log' ? <FileTextOutlined /> : <SyncOutlined />}
                        </span>
                        <div className="chg-hist__body">
                            <div className="chg-hist__text">{renderActivityText(a.reason, a)}</div>
                            <div className="chg-hist__time">{formatDateAndTimeFromNowWithTooltip(a.modified)}</div>
                        </div>
                        {a.log !== '' && a.log != null && (
                            <Button
                                className="chg-hist__log"
                                size="small"
                                onClick={() => { setSelectedLog(a.log); setIsModalOpen(true); }}
                            >
                                View log
                            </Button>
                        )}
                    </div>
                ))}
            </div>

            <Modal
                title="Activity log"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                width={1000}
                footer={[
                    <Tooltip title="Copy log" key="copy">
                        <Button icon={<CopyOutlined />} onClick={copyLog}>Copy</Button>
                    </Tooltip>,
                    <Button key="close" type="primary" onClick={() => setIsModalOpen(false)}>Close</Button>,
                ]}
            >
                <pre className="chg-hist__logbox">{selectedLog}</pre>
            </Modal>
        </>
    );
};

export default ChangeHistory;
